import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HelperService } from 'src/app/helper.service';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types';
import { ImsService } from '../../ims.service';
import { HostDetailsService } from '../host-details.service';
import { resolveDomainValidationErrorMessage } from 'src/app/shared/domain-validation/domain-validation-messages';

const HOST_PROFILE_TYPE = 'host';

@Component({
    selector: 'app-host-account',
    templateUrl: './host-account.component.html',
    styleUrls: ['./host-account.component.css']
})
export class HostAccountComponent implements OnInit, OnChanges {
    @Input() data;
    public info: any;
    public newEmail = '';
    public updatingEmail = false;
    public errorMsg = '';
    public corporateEmailChecked = false;
    public corporateEmailDisabled = false;
    public showEmailDialog = false;
    public isEmailVerificationInProgress = false;
    public domainType = '';
    public domainTypeLookupLoading = false;
    public readonly domainTypeOptions = ['corporate', 'non_corporate'];
    public walletBalance: string | null = null;
    public walletBalanceLoading: boolean = false;
    public walletBalanceError: boolean = false;
    constructor(
        private imsService: ImsService,
        private toasterService: ToasterService,
        private hostDetailsService: HostDetailsService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.data && changes.data.currentValue && changes.data.currentValue.dataFetched === false) {
            this.blankCard();
        } else if (changes.data && changes.data.currentValue) {
            this.data = changes.data.currentValue;
            this.constructCard();
            // Set checkbox state based on email only if not in verification process
            if (!this.isEmailVerificationInProgress) {
                const host = this.data.profiles.find(profile => profile.type === HOST_PROFILE_TYPE);
                this.corporateEmailChecked = !!(host && host.email);
                this.corporateEmailDisabled = !!(host && host.email);
            }
            if (this.data && this.data.dataFetched === true && this.data.profiles) {
                const hostProfile = this.data.profiles.find(profile => profile.type === HOST_PROFILE_TYPE);
                if (hostProfile && hostProfile.id) {
                    this.fetchWalletBalance();
                }
            }
        } else {
            this.emptyCard();
        }
    }
    ngOnInit() {}

    blankCard() {
        this.info = {
            header: 'Hopr account',
            dataFetched: false,
            data: []
        };
    }

    emptyCard() {
        this.info = {
            header: 'Hopr account',
            dataFetched: false,
            data: null
        };
    }

    constructCard() {
        if (this.data) {
            const host = this.data.profiles.find(profile => profile.type === HOST_PROFILE_TYPE);
            const cardData = [
                {
                    header: 'Current Status',
                    className: host.status === "ACTIVE" ? 'green-badge' : 'red-badge',
                    value: host.status === "ACTIVE" ? 'Active' : 'Inactive',
                    information: !host.status
                }
            ];

            const walletBalanceCardData = HelperService.buildWalletBalanceCardData(
                this.walletBalance,
                this.walletBalanceLoading,
                this.walletBalanceError
            );
            if (walletBalanceCardData) {
                cardData.push(walletBalanceCardData as any);
            }

            // Add corporate email checkbox row
            cardData.push({ header: this.corporateEmailLabel, isCheckbox: true } as any);

            // Add email updated by row
            if (host) {
                cardData.push({
                    header: 'Email Updated By',
                    value: host.updatedBy || 'N/A'
                } as any);
            }

            this.info = {
                header: 'Hopr account',
                dataFetched: true,
                data: cardData,
                reload: true
            };
        }
    }

    formatDate(date) {
        return HelperService.dateString(date);
    };

    // --- Checkbox handler ---
    onCorporateEmailCheckboxChange(event: any) {
        if (event.target.checked && !this.corporateEmailDisabled) {
            this.isEmailVerificationInProgress = true;
            this.showEmailDialog = true;
            this.newEmail = '';
            this.resetDomainTypeSelection();
            this.errorMsg = '';
        }
    }

    resetDomainTypeSelection() {
        this.domainType = '';
        this.domainTypeLookupLoading = false;
    }

    canLookupEmail(): boolean {
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        return trimmedEmail.indexOf('@') > 0;
    }

    lookupEmailDomain() {
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        if (!trimmedEmail || trimmedEmail.indexOf('@') <= 0) {
            this.resetDomainTypeSelection();
            return;
        }

        this.domainTypeLookupLoading = true;
        this.imsService.lookupEmailDomain(trimmedEmail).subscribe({
            next: (response: any) => {
                this.domainTypeLookupLoading = false;
                if (response && response.exists && response.domainType) {
                    this.domainType = response.domainType;
                } else {
                    this.domainType = '';
                }
            },
            error: () => {
                this.domainTypeLookupLoading = false;
                this.domainType = '';
            }
        });
    }

    // --- Dialog close handler ---
    closeEmailDialog() {
        this.showEmailDialog = false;
        this.isEmailVerificationInProgress = false;
        // Uncheck the box when cancelled
        this.corporateEmailChecked = false;
    }

    // --- Submit email from dialog ---
    submitEmail() {
        if (!this.data || !this.data.profiles) return;
        const hostProfile = this.data.profiles.find(profile => profile.type === HOST_PROFILE_TYPE);
        if (!hostProfile || !this.newEmail) {
            this.errorMsg = 'Please enter a valid email.';
            return;
        }
        if (!this.domainType) {
            this.errorMsg = 'Please select a domain type.';
            return;
        }
        this.updatingEmail = true;
        this.errorMsg = '';
        // Simulate API call for updating email (replace with real service if available)
        this.imsService.updateUserEmail(this.data.id, this.newEmail, ['host'], this.domainType)
          .subscribe({
            next: (res: any) => {
              this.showEmailDialog = false;
              this.isEmailVerificationInProgress = false;
              this.updatingEmail = false;
              this.errorMsg = '';
              hostProfile.email = this.newEmail;
              this.constructCard();
              this.corporateEmailChecked = true;
              this.corporateEmailDisabled = true;
              // Show a success toaster
              this.toasterService.showToaster(new Toaster({
                type: ToasterType.SUCCESS,
                message: 'Corporate email verified successfully!'
              }));
              window.location.reload();
            },
            error: (err) => {
              this.updatingEmail = false;
              this.errorMsg = resolveDomainValidationErrorMessage(err);
              // Don't reset isEmailVerificationInProgress on error, let user try again or cancel
            }
          });
    }

    get showVerifyEmailToggle(): boolean {
        if (!this.info || !this.info.data) return false;
        const emailField = this.info.data.find(d => d.header === 'Email');
        return !emailField || !emailField.value || emailField.value === 'N/A';
    }

    get corporateEmailLabel(): string {
        if (this.corporateEmailChecked && this.corporateEmailDisabled) {
            return 'Corporate Email (Verified)';
        }
        return 'Corporate Email (Not Verified)';
    }

    fetchWalletBalance() {
        if (!this.data || !this.data.profiles) {
            return;
        }
        const hostProfile = this.data.profiles.find(profile => profile.type === HOST_PROFILE_TYPE);
        if (!hostProfile || !hostProfile.id) {
            return;
        }

        this.walletBalanceLoading = true;
        this.walletBalanceError = false;

        this.hostDetailsService.getHostWalletBalance(hostProfile.id).subscribe({
            next: (response: any) => {
                this.walletBalanceLoading = false;
                let balance = null;
                if (response && 'balance' in response) {
                    balance = response.balance;
                }

                if (balance !== null && balance !== undefined) {
                    const formattedBalance = parseFloat(balance).toFixed(2);
                    this.walletBalance = formattedBalance;
                    this.walletBalanceError = false;
                } else {
                    this.walletBalance = 'Unable to fetch';
                    this.walletBalanceError = true;
                }
                this.constructCard();
            },
            error: (err) => {
                this.walletBalanceLoading = false;
                this.walletBalanceError = true;
                this.walletBalance = 'Unable to fetch';
                this.constructCard();
            }
        });
    }

    refreshWalletBalance() {
        this.walletBalanceError = false;
        this.fetchWalletBalance();
    }

    onRefresh() {
        this.refreshWalletBalance();
    }
}
